import { SearchableMultiselect, SearchableMultiselectOption } from 'components';
import useTimeZone from 'hooks/use-timezone';
import { FC, useCallback, useMemo } from 'react';

interface HourSelectProps {
  id: string;
  hours: number[];
  setHours: (hours: number[]) => void;
}

const HourSelect: FC<HourSelectProps> = ({ id, hours, setHours }) => {
  const { timeZoneOffset } = useTimeZone();

  const utcHours = useMemo(() => new Set(hours), [hours]);

  const options: SearchableMultiselectOption[] = useMemo(() => {
    return Array(24)
      .fill(0)
      .map((_, hour) => (hour - timeZoneOffset) % 24)
      .map((utcHour, hour) => ({
        id: utcHour,
        value: `${hour % 12 || 12}:00 ${hour < 12 ? 'AM' : 'PM'}`,
        selected: utcHours.has(utcHour)
      }));
  }, [utcHours, timeZoneOffset]);

  const handleSelect = useCallback(
    (option: SearchableMultiselectOption) => {
      const utcHoursCopy = new Set(utcHours);
      if (!utcHoursCopy.delete(option.id)) {
        utcHoursCopy.add(option.id);
      }
      setHours([...utcHoursCopy]);
    },
    [utcHours, setHours]
  );

  return (
    <div className="grid-4 mt-2" key={id}>
      <div className="text-field">
        <SearchableMultiselect error="" options={options} onSelect={handleSelect} data-cy={`${id}-input`} />
      </div>
      <div className="col-span-3 d-flex flex-wrap g-2" key={`${id}-selected-options`}>
        {options
          .filter(option => option.selected)
          .map(option => (
            <div
              className="tag g-2"
              key={`${id}-selected-option-${option.id}`}
              data-cy={`${id}-tag-${option.value.replace(/:.+/, '')}`}
            >
              <p className="text-primary">{option.value}</p>
              <button type="button" className="tag-close" onClick={() => handleSelect(option)}>
                <span className="material-icons">close</span>
              </button>
            </div>
          ))}
      </div>
    </div>
  );
};

export default HourSelect;
